import React, {useState, useEffect} from "react";
import {useAuth} from "../contexts/authContext";
import {
    Button,
    Typography,
    message,
    Card,
    Space,
    Row,
    Col,
    Tag,
    InputNumber,
    Checkbox, Alert, Spin, Input,
} from "antd";
import {useLocation} from "react-router-dom";
import {setUserId} from "../config/amplitude/amplitude";
import {CheckCircleOutlined, CloseCircleOutlined} from "@ant-design/icons";
import {useCvBalanceStore} from "../store/useCvBalanceStore";
import {sendMessageToTelegram} from "../service/telegramService";

const {Title, Text} = Typography;

const BalancePage = () => {
    const {currentUser} = useAuth();
    const location = useLocation();
    const [topUpLoading, setTopUpLoading] = useState(false);
    const [topupStatus, setTopupStatus] = useState<string | null>(null);
    const [linkedinPostUrl, setLinkedinPostUrl] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const {
        cvSent,
        sendsInPackage,
        packageType,
        fastTrackEnabled,
        expirationDate,
        isLoading,
        fetchBalance
    } = useCvBalanceStore();

    useEffect(() => {
        document.title = "Balance - CV Postman App";
        localStorage.setItem("lastOpenedPage", "/balance");
        if (currentUser?.email) setUserId(currentUser.email);
    }, [currentUser]);

    useEffect(() => {
        // Extract query parameters from URL
        const params = new URLSearchParams(location.search);
        const topup = params.get("topup");

        if (topup === "success" || topup === "failed") {
            setTopupStatus(topup);
        }
    }, [location.search]);

    const plans = [
        {
            title: "Basic",
            price: "1.98",
            pricePerUnit: 0.1,
            popular: false,
            available: true,
            features: [
                {included: true, text: "Автоматична подача CV на dou.ua"},
                {included: true, text: "Автоматична подача CV на djinni.co"},
                {included: true, text: "Розсилка CV за розкладом Пн-Пт"},
                {included: true, text: "Налаштування критеріїв пошуку"},
                {included: true, text: "Статичний cover letter"},
                {included: true, text: "Звіт по вакансіях"},
                {included: false, text: "AI-генерація супровідного листа (+20% відповідей)"},
                {included: false, text: "CV розсилка fast track - кожної години (+25% відповідей)"},
                {included: false, text: "AI-оптимізація CV (x3 відповідей)"},
                {included: false, text: "Розширена аналітика"},
            ],
            buttonText: "Спробувати Basic",
        },
        {
            title: "Pro",
            price: "2.99",
            pricePerUnit: 0.2,
            popular: true,
            available: true,
            features: [
                {included: true, text: "Автоматична подача CV на dou.ua"},
                {included: true, text: "Автоматична подача CV на djinni.co"},
                {included: true, text: "Розсилка CV за розкладом Пн-Пт"},
                {included: true, text: "Налаштування критеріїв пошуку"},
                {included: true, text: "Статичний cover letter"},
                {included: true, text: "Звіт по вакансіях"},
                {included: true, text: "AI-генерація супровідного листа (+20% відповідей)"},
                {included: false, text: "CV розсилка fast track - кожної години (+25% відповідей)"},
                {included: false, text: "AI-оптимізація CV (x3 відповідей)"},
                {included: false, text: "Розширена аналітика"},
            ],
            buttonText: "Спробувати Pro",
        },
        {
            title: "Premium",
            price: "4.54",
            pricePerUnit: 0.3,
            popular: false,
            available: false,
            features: [
                {included: true, text: "Автоматична подача CV на dou.ua"},
                {included: true, text: "Автоматична подача CV на djinni.co"},
                {included: true, text: "Розсилка CV за розкладом Пн-Пт"},
                {included: true, text: "Налаштування критеріїв пошуку"},
                {included: true, text: "Статичний cover letter"},
                {included: true, text: "Звіт по вакансіях"},
                {included: true, text: "AI-генерація супровідного листа (+20% відповідей)"},
                {included: true, text: "CV розсилка fast track - кожної години (+25% відповідей)"},
                {included: true, text: "AI-оптимізація CV (x3 відповідей)"},
                {included: true, text: "Розширена аналітика"},
            ],
            buttonText: "Спробувати Premium",
        },
    ];

    const initialPackageState = {
        count: 50,
        fastTrack: false,
        cvType: 'static' as 'static' | 'ai',
    };

    const [packageStates, setPackageStates] = useState<Record<string, typeof initialPackageState>>(
        () =>
            plans.reduce((acc, plan) => {
                acc[plan.title] = {...initialPackageState};
                return acc;
            }, {} as Record<string, typeof initialPackageState>)
    );

    const changeCount = (planTitle: string, delta: number) => {
        setPackageStates((prev) => {
            const current = prev[planTitle];
            const newCount = Math.max(1, current.count + delta);
            return {
                ...prev,
                [planTitle]: {
                    ...current,
                    count: newCount,
                },
            };
        });
    };

    const toggleFastTrack = (planTitle: string, value: boolean) => {
        setPackageStates((prev) => ({
            ...prev,
            [planTitle]: {
                ...prev[planTitle],
                fastTrack: value,
            },
        }));
    };

    const handleTopUp = async (planTitle: string) => {
        setTopUpLoading(true);
        const {count, fastTrack, cvType} = packageStates[planTitle];
        const pricePerUnit = plans.find(p => p.title === planTitle)?.pricePerUnit || 0.15;
        const baseTotal = count * pricePerUnit;
        const total = baseTotal + (fastTrack ? 20 : 0);

        try {
            const response = await fetch(`${process.env.REACT_APP_PAYMENT_API}/api/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: currentUser?.email,
                    count,
                    fastTrack,
                    planTitle,
                    pricePerUnit,
                    totalAmount: total.toFixed(2),
                }),
            });

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url; // redirect to Stripe
            }
        } catch (error) {
            message.error("Сталася помилка під час створення сесії Stripe");
            console.error(error);
        } finally {
            setTopUpLoading(false);
        }
    };

    const handleLinkedinSubmit = async () => {
        if (!linkedinPostUrl) return;

        await sendMessageToTelegram({
            email: currentUser.email,
            messageText: `🎁 LinkedIn Post Submitted:\n🔗 ${linkedinPostUrl}`,
        });

        setSubmitted(true);
        message.success('Посилання на пост прийнято. Очікуйте нарахування CV 😊');
    };



    return (
        <section style={{background: '#f9f9fb', padding: '64px 0'}}>
            <div style={{maxWidth: 1100, margin: '0 auto', padding: '0 16px'}}>

                {(isLoading || (cvSent !== null && sendsInPackage !== null)) && (
                    <Card
                        style={{
                            marginBottom: 32,
                            background: '#fefefe',
                            border: '1px solid #e0e0e0',
                            borderRadius: 12,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                        }}
                        bodyStyle={{padding: 24}}
                    >
                        <div style={{position: 'relative', zIndex: 0}}>
                            <Spin spinning={isLoading} style={{zIndex: 0}}>
                                <Title level={4} style={{marginBottom: 24}}>
                                    📊 Поточний баланс
                                </Title>
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} sm={6}>
                                        <Text type="secondary">📦 Тариф</Text>
                                        <div style={{fontWeight: 500, fontSize: 16}}>
                                            {packageType || 'Free'}
                                        </div>
                                    </Col>

                                    <Col xs={24} sm={6}>
                                        <Text type="secondary">📬 Баланс CV</Text>
                                        <div style={{fontWeight: 500, fontSize: 16}}>
                                            {cvSent} / {sendsInPackage}
                                        </div>
                                    </Col>

                                    <Col xs={24} sm={6}>
                                        <Text type="secondary">⚡ Fast-track</Text>
                                        <div>
                                            <Tag color={fastTrackEnabled ? 'green' : 'default'} style={{fontSize: 14}}>
                                                {fastTrackEnabled ? 'Увімкнено' : 'Вимкнено'}
                                            </Tag>
                                        </div>
                                    </Col>

                                    {fastTrackEnabled && (
                                        <Col xs={24} sm={6}>
                                            <Text type="secondary">🕒 Fast-track до</Text>
                                            <div style={{fontWeight: 500, fontSize: 16}}>
                                                {expirationDate
                                                    ? new Date(expirationDate).toLocaleDateString('uk-UA', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                    })
                                                    : '—'}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Spin>
                        </div>
                    </Card>
                )}


                {topupStatus === "success" && (
                    <Alert style={{marginTop: 16}}
                           message="Top-up was successful! Balance will be updated witin next few minutes"
                           type="success"
                           showIcon/>
                )}

                {topupStatus === "failed" && (
                    <Alert style={{marginTop: 16}} message="Top-up failed. Please try again or contact customer support"
                           type="error" showIcon/>
                )}
                <Card
                    style={{
                        marginTop: 48,
                        borderRadius: 12,
                        background: "#fff",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}
                    bodyStyle={{ padding: 24 }}
                >
                    <Title level={4} style={{ marginBottom: 16 }}>💸 Отримай 100 CV відправок безкоштовно!</Title>
                    <Text>
                        Поділись своїми враженнями про <b>CV Postman</b> у LinkedIn, додай лінк <code>cv-postman.com</code> у пост,
                        і отримай +100 CV відправок!
                    </Text>

                    <div style={{ marginTop: 16 }}>
                        <Button
                            type="default"
                            target="_blank"
                            href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fcv-postman.com"
                        >
                            ✨ Створити пост в LinkedIn
                        </Button>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <Text strong>Встав лінк на твій пост:</Text>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                            <Input
                                placeholder="https://www.linkedin.com/posts/..."
                                value={linkedinPostUrl}
                                onChange={(e) => setLinkedinPostUrl(e.target.value)}
                                style={{ flex: 1, minWidth: 260 }}
                                disabled={submitted}
                            />
                            <Button
                                type="primary"
                                onClick={handleLinkedinSubmit}
                                disabled={!linkedinPostUrl || submitted}
                            >
                                Надіслати
                            </Button>
                        </div>
                        {submitted && (
                            <Alert
                                style={{ marginTop: 16 }}
                                type="success"
                                message="Дякуємо! Ми перевіримо пост і нарахуємо CV протягом доби 💙"
                                showIcon
                            />
                        )}
                    </div>
                </Card>
                <div style={{textAlign: 'center', marginBottom: 48}}>
                    <Title level={2} style={{marginBottom: 8}}>або</Title>
                    <Title level={2} style={{marginBottom: 8}}>💼 Обери свій тариф</Title>
                    <Text type="secondary">Налаштуй кількість CV надсилань і отримай більше інтерв'ю</Text>
                </div>

                <div style={{position: 'relative', zIndex: 0}}>
                    <Spin spinning={topUpLoading} tip="Перенаправлення до Stripe..." style={{zIndex: 0}}>
                        <Row gutter={[24, 32]}>
                            {plans.map((plan) => {
                                const {count, fastTrack, cvType} = packageStates[plan.title];
                                const pricePerUnit = plan.pricePerUnit;
                                const baseTotal = count * pricePerUnit;
                                const total = baseTotal + (fastTrack ? 20 : 0);

                                return (
                                    <Col xs={24} md={8} key={plan.title}>
                                        <Card
                                            hoverable
                                            title={
                                                <div style={{textAlign: 'center', fontSize: 20, fontWeight: 600}}>
                                                    {plan.title}
                                                </div>
                                            }
                                            bordered={false}
                                            style={{
                                                borderRadius: 16,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                                                overflow: 'hidden',
                                                background: '#fff',
                                                opacity: plan.available ? 1 : 0.6,
                                                pointerEvents: plan.available ? 'auto' : 'none',
                                                position: 'relative',
                                            }}
                                            headStyle={{
                                                background: plan.popular ? '#f4f0ff' : '#fafafa',
                                            }}
                                            extra={
                                                plan.popular ? (
                                                    <Tag color="purple" style={{fontWeight: 600}}>🔥 Рекомендований</Tag>
                                                ) : !plan.available ? (
                                                    <Tag color="gold" style={{fontWeight: 600}}>⏳ Coming soon</Tag>
                                                ) : null
                                            }
                                        >
                                            {/*<div style={{textAlign: 'center', marginBottom: 24}}>
                                        <Title level={3} style={{margin: 0}}>${plan.price}</Title>
                                        <Text type="secondary">/ тиждень</Text>
                                    </div>*/}
                                            <Space direction="vertical" size="large" style={{width: "100%"}}>
                                                <div style={{
                                                    background: '#fafafa',
                                                    padding: '16px 20px',
                                                    borderRadius: 12,
                                                    border: '1px solid #e0e0e0'
                                                }}>
                                                    <div style={{marginBottom: 16}}>
                                                        <Text strong>Кількість CV відправок:</Text>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                            marginTop: 8
                                                        }}>
                                                            <Button
                                                                shape="circle"
                                                                onClick={() => changeCount(plan.title, -10)}
                                                                disabled={count <= 1}
                                                                type="default"
                                                            >
                                                                −
                                                            </Button>
                                                            <InputNumber
                                                                min={1}
                                                                value={count}
                                                                onChange={(value) => changeCount(plan.title, (value || 1) - count)}
                                                                style={{width: 100}}
                                                            />
                                                            <Button
                                                                shape="circle"
                                                                onClick={() => changeCount(plan.title, +10)}
                                                                type="default"
                                                            >
                                                                +
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    <Checkbox
                                                        checked={fastTrack}
                                                        onChange={(e) => toggleFastTrack(plan.title, e.target.checked)}
                                                        style={{marginBottom: 16}}
                                                    >
                                                        <Text strong>Fast-track</Text> (щогодини) <Text
                                                        type="success"><b>+
                                                        $20</b></Text>
                                                    </Checkbox>

                                                    <div style={{
                                                        background: '#fff',
                                                        padding: 16,
                                                        borderRadius: 8,
                                                        border: '1px dashed #d0d0d0'
                                                    }}>
                                                        <Space direction="vertical" size="small"
                                                               style={{width: '100%'}}>
                                                            <Text>Ціна за 1: <b>${pricePerUnit.toFixed(2)}</b></Text>
                                                            <Text>Fast-track: {fastTrack ?
                                                                <b>$20.00</b> : '$0.00'}</Text>
                                                            <Text strong style={{fontSize: 18}}>
                                                                Загальна сума: <span
                                                                style={{color: '#1677ff'}}>${total.toFixed(2)}</span>
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                </div>

                                            </Space>

                                            <ul style={{paddingLeft: 0, listStyle: 'none', marginBottom: 24}}>
                                                {plan.features.map((f, i) => {
                                                    // Автоматично активувати fast track опцію при ввімкненому чекбоксі
                                                    const isFastTrackFeature = f.text.includes("CV розсилка fast track");
                                                    const isIncluded = isFastTrackFeature ? fastTrack : f.included;

                                                    return (
                                                        <li key={i}
                                                            style={{
                                                                marginBottom: 12,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}>
                                                            {isIncluded ? (
                                                                <CheckCircleOutlined
                                                                    style={{color: 'green', marginRight: 8}}/>
                                                            ) : (
                                                                <CloseCircleOutlined
                                                                    style={{color: '#ccc', marginRight: 8}}/>
                                                            )}
                                                            <Text
                                                                type={isIncluded ? undefined : 'secondary'}>{f.text}</Text>
                                                        </li>
                                                    );
                                                })}

                                            </ul>

                                            <Button
                                                type="primary"
                                                block
                                                size="large"
                                                disabled={topUpLoading}
                                                onClick={() => handleTopUp(plan.title)}
                                            >
                                                Поповнити
                                            </Button>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Spin>
                </div>


            </div>
        </section>

    );
};

export default BalancePage;
